import React, { FC, useState, useEffect, useRef } from 'react'
import { useLocation, useParams, useHistory } from 'react-router'
import { Box, Text, Heading, Flex, Button } from '@chakra-ui/core'
import { useRecoilValue, useRecoilState } from 'recoil'
import { useTranslation } from 'react-i18next'
import { configState, orderConfigState } from '../../recoil/atoms'
import { isMobileOnly } from 'react-device-detect'
import { BoxLinkButton, LinkButton } from '../../lib/types'
import { useQuery } from '@apollo/client'
import { Counter, ImageBox, UnitSelector } from '@engyalo/storefront-components'
import { Price } from '../../components/Price'
import { Hook, IStorefrontSettings } from '@engyalo/storefront-types'
import { cartState } from '../../recoil/atoms'
import Plugin, { usePlugin } from '../../Plugin'
import { ICartDictionary, ICartStats, IProduct } from '@engyalo/storefront-types/lib/plugins/model'
import { cartStatsState } from '../../recoil/selectors'
import { searchProductsByIdQuery } from '../../queries'
import {
  IProductsPricedPerCollectionVariables,
  IProductsPricedPerCollection,
  IProductPricedPerCollectionLevel1 as productLevel1Type,
} from '@engyalo/storefront-types/lib/graphql/product'
import { getStoreFront_orderConfig as IOrderConfig } from '@engyalo/storefront-types/lib/graphql/storefront'
import { generateCartId } from '../../lib/cartIdGenerator'
import { getTotalPrice } from '../../lib/getTotalPrice'
import { CartIcon } from '../../components/CartIcon'
import { getActionScreen } from '../../utils/analytics'
import { addProduct, removeProduct, updateQuantity } from '@engyalo/storefront-events'
import { ILocationSearch, skipIfInPreviewMode } from '../../utils/mode'

interface ProductUnitType {
  type: string
  interval: number
  format: string
}

const DEFAULT_PRODUCTS_DEPTH = 2
const DEFAULT_UNIT_TYPE = {
  type: 'default',
  interval: 1,
  format: '#0',
}

export const Product: FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation<ILocationSearch>()
  const { customer, order } = useParams()
  const config = useRecoilValue(configState)
  const [cart, setCart] = useRecoilState<ICartDictionary>(cartState)
  const [canAddToCart, setCanAddToCart] = useState(true)
  const cartStats = useRecoilValue<ICartStats>(cartStatsState)
  const [productToAddToCart, setproductToAdd] = useState({} as IProduct)
  const [priceEnabled, setPriceEnabled] = useState(false)
  const [productData, setProductData] = useState({} as productLevel1Type)
  const orderConfig = useRecoilValue<IOrderConfig>(orderConfigState)
  const [verticalHeight, setVerticalHeight] = useState(window.innerHeight * 0.01)
  const storefrontPlugin = usePlugin(Hook.STOREFRONT)
  const depth = (storefrontPlugin?.props as IStorefrontSettings)?.productsDepth ?? DEFAULT_PRODUCTS_DEPTH
  const [unitType, setUnitType] = useState<ProductUnitType>(DEFAULT_UNIT_TYPE)
  const [customUnitQuantity, setCustomUnitQuantity] = useState<Record<string, number>>({})

  const session = {
    userID: orderConfig.userID || '',
    customer: config.customer,
  }

  const {
    state: { product },
  } = useLocation()

  useEffect((): void => {
    const cartId = generateCartId(product)
    const productInCart = cart[cartId] ?? product
    setproductToAdd({
      ...productInCart,
      cartId,
    })

    const unitType =
      productInCart?.metadata?.unitTypes?.find(
        (unitType: ProductUnitType) => unitType.type === productInCart?.sizeDescription
      ) || productInCart.metadata?.unitTypes?.[0]
    if (unitType) {
      setUnitType(unitType)
    }

    console.log('product in cart', productInCart)

    if (productInCart?.quantity && productInCart.quantity > 0) {
      setCustomUnitQuantity((prevCustomQty) => ({ ...prevCustomQty, [unitType.type]: productInCart.quantity }))
    }
  }, [product])

  const { loading: loadingProduct } = useQuery<IProductsPricedPerCollection, IProductsPricedPerCollectionVariables>(
    searchProductsByIdQuery(depth),
    {
      variables: {
        name: customer,
        configId: order,
        productId: product.id,
      },
      skip: !customer,
      onCompleted(data: IProductsPricedPerCollection) {
        const currentCollection = data?.productsPricedPerCollection?.[0]

        setPriceEnabled(currentCollection?.enabledPrice === null ? true : !!currentCollection?.enabledPrice)
        setProductData(currentCollection?.productsPriced?.[0] ?? productData)
      },
      returnPartialData: true,
      fetchPolicy: 'no-cache',
    }
  )

  const getProductFirstImage = (product: productLevel1Type): string => {
    if (product && product.imageURL) return product.imageURL[0] || ''
    return ''
  }

  const addCustomUnitToCart = (
    product: IProduct,
    unitType: ProductUnitType,
    quantity: number,
    shouldReplace = false
  ) => {
    if (!canAddToCart) {
      return
    }

    const tempCart = { ...cart }
    if (product && product.id) {
      const currentSku = generateCartId(product)
      if (tempCart[currentSku]) {
        const tempProd = { ...tempCart[currentSku] }
        if (tempProd.quantity) {
          const totalPrice = getTotalPrice(product)
          tempProd.quantity = shouldReplace ? quantity : tempProd.quantity + quantity

          if (tempProd.quantity > 0) {
            tempCart[currentSku] = {
              ...tempProd,
              totalPrice,
              sizeDescription: unitType.type,
            }
            setCart(tempCart)
          } else {
            delete tempCart[currentSku]
            setCart({ ...tempCart })
          }
        }
      } else if (quantity > 0) {
        const totalPrice = getTotalPrice(product)

        tempCart[currentSku] = {
          ...product,
          totalPrice,
          quantity: quantity,
          cartId: currentSku,
          sizeDescription: unitType.type,
        } as IProduct
        setCart(tempCart)
      }
    }
  }

  const addToCart = (product: IProduct, quantity = 1, backToCatalogue = false, shouldReplace = false): void => {
    if (canAddToCart) {
      const tempCart = { ...cart }
      if (product && product.id) {
        const currentSku = generateCartId(product)
        if (tempCart[currentSku]) {
          const tempProd = { ...tempCart[currentSku] }
          if (tempProd.quantity) {
            const totalPrice = getTotalPrice(product)
            tempProd.quantity = shouldReplace ? quantity : tempProd.quantity + quantity

            if (tempProd.quantity > 0) {
              tempCart[currentSku] = {
                ...tempProd,
                totalPrice,
              }
              setCart(tempCart)
            } else {
              delete tempCart[currentSku]
              setCart({ ...tempCart })
            }
          }
        } else if (quantity > 0) {
          const totalPrice = getTotalPrice(product)

          tempCart[currentSku] = {
            ...product,
            totalPrice,
            quantity: quantity,
            cartId: currentSku,
          } as IProduct
          setCart(tempCart)
        }

        skipIfInPreviewMode(() => {
          if (shouldReplace) {
            updateQuantity({
              product: {
                id: product.id || '',
                sku: product.sku || '',
                name: product.name || '',
                category: product.category || '',
                price: product.price || 0,
                note: product.note || '',
                quantity,
                screen: getActionScreen(location),
              },
              session,
            })
          } else {
            if (quantity > 0) {
              addProduct({
                product: {
                  id: product.id || '',
                  sku: product.sku || '',
                  name: product.name || '',
                  category: product.category || '',
                  price: product.price || 0,
                  note: product.note || '',
                  screen: getActionScreen(location),
                },
                session,
              })
            } else {
              removeProduct({
                product: {
                  id: product.id || '',
                  sku: product.sku || '',
                  name: product.name || '',
                  category: product.category || '',
                  price: product.price || 0,
                  note: product.note || '',
                  screen: getActionScreen(location),
                },
                session,
              })
            }
          }
        })

        if (backToCatalogue) {
          history.push({
            pathname: `/${customer}/${order}`,
            search: location.search,
          })
        }
      }
    }
  }

  useEffect(() => {
    function handleResize() {
      const newVerticalHeight = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${newVerticalHeight}px`)
      setVerticalHeight(newVerticalHeight)
    }

    window.addEventListener('resize', handleResize)
  })

  if (!product) {
    return <div>no product</div>
  }

  const unitTypes = product.metadata?.unitTypes
  const hasUnitTypes = unitTypes && unitTypes.length > 0
  const canAddCustomUnitToCart = customUnitQuantity[unitType.type] > 0

  return (
    <Box h={isMobileOnly ? `calc(var(--vh, 1${verticalHeight}) * 100)` : '100%'} w="100%" d="flex" flexDir="column">
      <Box
        h="4.0rem"
        d="flex"
        justifyContent="space-between"
        paddingX="1.2rem"
        top={0}
        flex="0 0 auto"
        position="sticky"
        background="white"
        maxW="42.5rem"
        width="100%"
        zIndex={2}
      >
        <LinkButton
          to={{ pathname: `/${customer}/${order}`, search: location.search }}
          h="100%"
          w="3.2rem"
          background="transparent"
          borderColor="transparent"
          size="lg"
          aria-label="cart"
          icon="chevron-left"
        />
        <CartIcon />
      </Box>
      {/* Body */}
      <Plugin
        hook={Hook.PRODUCT_DETAIL_BODY}
        pluginProps={{
          product: productData,
          isLoading: loadingProduct,
          cart,
          config,
          enablePrice: priceEnabled,
          setCanAddToCart,
          tFunction: t,
          setproductToAdd,
        }}
      >
        <>
          <Box paddingX="1.6rem" marginTop="4rem" height="100%" overflowY="scroll">
            <ImageBox imageUrl={getProductFirstImage(productToAddToCart)} />
            <Heading marginY="4px" as="h3" fontSize="xl" flex="1">
              {productToAddToCart.name}
            </Heading>
            <Text fontSize="md" fontFamily="body" color="gray.100" paddingBottom="0.4rem">
              {productToAddToCart.description}
            </Text>
            {priceEnabled && (
              <Price
                price={productToAddToCart.price}
                currency={config.currencyPrefix}
                currencyFormat={config.currencyFormat}
              />
            )}
          </Box>
          <Flex flex="1" flexDirection="column" overflow="auto" marginTop=".8rem" paddingX=".4rem" />
        </>
      </Plugin>
      {/* Body */}
      {/* Footer */}
      <Plugin
        hook={Hook.PRODUCT_ADD_TO_CART}
        pluginProps={{
          product: productToAddToCart,
          config,
          addToCart,
          cartStats,
          cart,
          tFunction: t,
          canAddToCart,
          orderConfig,
        }}
      >
        <Box
          zIndex={1}
          paddingY="1.2rem"
          paddingX="0.8rem"
          boxShadow="md"
          backgroundColor="white"
          position="sticky"
          bottom={0}
          flex="0 0 auto"
        >
          {hasUnitTypes ? (
            <UnitSelector
              unitTypes={unitTypes as ProductUnitType[]}
              defaultActive={unitType}
              onChange={(selectedUnitType: ProductUnitType) => {
                setUnitType(selectedUnitType)
              }}
            />
          ) : null}
          <Flex justifyContent={hasUnitTypes ? 'space-between' : 'center'} alignItems="center" px="2">
            <Box flex="1" maxW="50%">
              {hasUnitTypes ? (
                <Counter
                  name={productToAddToCart.cartId || ''}
                  format={unitType.format}
                  addAction={() => {
                    setCustomUnitQuantity((qty) => ({
                      ...qty,
                      [unitType.type]: (qty[unitType.type] || 0) + unitType.interval,
                    }))
                  }}
                  minusAction={() => {
                    setCustomUnitQuantity((qty) => ({
                      ...qty,
                      [unitType.type]: (qty[unitType.type] || 0) - unitType.interval,
                    }))
                  }}
                  onInputBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                    const quantity = parseInt(event.target.value)
                    setCustomUnitQuantity((qty) => ({ ...qty, [unitType.type]: quantity }))
                  }}
                  value={customUnitQuantity[unitType.type]}
                />
              ) : (
                <Counter
                  name={productToAddToCart.cartId || ''}
                  addAction={(event: React.MouseEvent<HTMLButtonElement>): void => {
                    event.stopPropagation()
                    addToCart(productToAddToCart, 1)
                  }}
                  minusAction={(event: React.MouseEvent<HTMLButtonElement>): void => {
                    event.stopPropagation()
                    addToCart(productToAddToCart, -1)
                  }}
                  onInputBlur={(event: React.FocusEvent<HTMLInputElement>): void => {
                    const quantity = parseInt(event.target.value)
                    addToCart(productToAddToCart, quantity, false, true)
                  }}
                  value={cart[productToAddToCart.cartId || ''] ? cart[productToAddToCart.cartId || ''].quantity : 0}
                />
              )}
            </Box>
            {hasUnitTypes ? (
              <BoxLinkButton
                to={{ pathname: `/${customer}/${order}`, search: location.search }}
                flex="2"
                maxW="66%"
                display="flex"
                marginLeft="4"
                paddingY="1.2rem"
                paddingX="1.6rem"
                backgroundColor={canAddCustomUnitToCart ? 'primary.500' : 'gray.500'}
                borderColor="transparent"
                fontWeight="500"
                color={canAddCustomUnitToCart ? 'white' : 'gray.50'}
                fontFamily="body"
                fontSize="1.1rem"
                borderRadius="0.4rem"
                textDecor="none"
                cursor={canAddCustomUnitToCart ? 'pointer' : 'not-allowed'}
                pointerEvents={canAddCustomUnitToCart ? 'auto' : 'none'}
                onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                  if (!canAddCustomUnitToCart) {
                    event.preventDefault()
                    return
                  }

                  addCustomUnitToCart(productToAddToCart, unitType, customUnitQuantity[unitType.type], true)
                }}
              >
                <Text flex="1" fontSize="lg" alignSelf="center" textAlign="center">
                  {t('Agregar')}
                </Text>
              </BoxLinkButton>
            ) : null}
          </Flex>
        </Box>
      </Plugin>
      {/* Footer */}
    </Box>
  )
}
